import './anfragen.css';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fmtDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AREA_LABEL } from './labels';
import { RequestDialog } from './RequestDialog';
import type { AnfragenTabResponse, KanzleiRequestItem } from './types';

// Reiter „Anfragen“ eines Mandanten: angeforderte Unterlagen meiner Bereiche, offene zuerst (die ältesten oben),
// erledigte zum Aufklappen. Eine Anfrage öffnet sich über ?anfrage=<id>, damit „Ansehen“ aus der Leiste direkt dorthin führt.
export function AnfragenTab({ clientId, both }: { clientId: string; both: boolean }) {
  const [params, setParams] = useSearchParams();
  const res = useApi<AnfragenTabResponse>(`/api/kanzlei/anfragen/${clientId}`, { live: ['anfragen'], clientId });
  const [showDone, setShowDone] = useState(false);
  const open = params.get('anfrage');
  const d = res.data;

  const show = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('anfrage', id);
    else next.delete('anfrage');
    setParams(next, { replace: true });
  };

  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;
  const offen = d.requests.filter((r) => r.status === 'offen');
  const done = d.requests.filter((r) => r.status === 'erledigt');

  return (
    <div className="anfragen">
      <div className="card">
        <div className="ch">
          <h3>Anfragen aus der App</h3>
          <span>{offen.length} offen</span>
        </div>
        {offen.length ? (
          offen.map((r) => <RequestRow key={r.id} r={r} area={both} onOpen={show} />)
        ) : (
          <p className="note">
            Keine offenen Anfragen. {d.client.contactName} fordert Unterlagen wie SuSa oder Verdienstbescheinigungen in der App
            an, sie erscheinen dann hier.
          </p>
        )}
        {done.length > 0 && (
          <button
            type="button"
            className="btn sm"
            style={{ marginTop: 10 }}
            aria-expanded={showDone}
            onClick={() => setShowDone((x) => !x)}
          >
            {showDone ? 'Erledigte ausblenden ▴' : `Erledigte Anfragen (${done.length}) ▾`}
          </button>
        )}
        {showDone && done.map((r) => <RequestRow key={r.id} r={r} area={both} onOpen={show} />)}
      </div>
      {open && <RequestDialog key={open} id={open} onClose={() => show(null)} />}
    </div>
  );
}

function RequestRow({ r, area, onOpen }: { r: KanzleiRequestItem; area: boolean; onOpen(id: string): void }) {
  const todo = r.status === 'offen' && (r.unseen || r.newComment);
  return (
    <div className="task">
      <span className="av" aria-hidden="true">
        📄
      </span>
      <div className="tx">
        <b>{r.label}</b>
        {r.employee && ` · ${r.employee.name}`}
        {/* wer beide Bereiche hat (oder der Admin), sieht, wohin die Anfrage gehört */}
        {area && <span className="note"> · {AREA_LABEL[r.area]}</span>}
        {r.unseen && (
          <>
            {' '}
            <span className="pill p-info">neu</span>
          </>
        )}
        {r.newComment && (
          <>
            {' '}
            <span className="pill p-warn">💬 neuer Kommentar</span>
          </>
        )}
        {r.status === 'erledigt' ? (
          <>
            {' '}
            <span className="pill p-ok">erledigt</span>
          </>
        ) : (
          r.files > 0 && (
            <>
              {' '}
              <span className="pill p-ok">Datei hochgeladen</span>
            </>
          )
        )}
        <small>
          {r.note ? `Wunsch: „${r.note}“ · ` : ''}angefordert am {fmtDate(r.createdAt)}
          {r.createdBy ? ` von ${r.createdBy}` : ''}
          {r.status === 'erledigt' &&
            ` · erledigt${r.doneAt ? ` am ${fmtDate(r.doneAt)}` : ''}${r.doneBy ? ` von ${r.doneBy}` : ''}`}
        </small>
      </div>
      <button type="button" className={`btn sm${todo ? ' dark' : ''}`} onClick={() => onOpen(r.id)}>
        Ansehen
      </button>
    </div>
  );
}
